import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { IReport } from '~/src/dash/api/console-api';
import {
  convertECurrencyTypeToString,
  convertStringToECurrencyType,
  getExchangeRate,
  IExchangeRate,
} from '~/src/dash/api/exchange-rates';
import { isTrue } from '~/src/dash/api/resource-transforms';
import { IHttpResponse } from '~/src/dash/api/rest';
import { getS3AccessPoint, IS3AccessPoint } from '~/src/dash/api/s3-access-points';
import { EBool } from '~/src/dash/api/types/bool';
import {
  getReportTypes,
  IReportType,
  ReportTypeID,
  reportTypeIDToTKey,
} from '~/src/dash/apiV2/reporting/reportTypes';
import {
  ISimpleOption,
  Panel,
  Panels,
  Row,
  Rows,
  spacingV2,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateUsingTimezoneV2 } from '~/src/dash/utils/date';
import {
  DateRangeInt,
  DateRangeIntToDateRange,
  findDateRange,
  generateQueryDates,
} from '~/src/dash/utils/dateTimeRanges';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { getTimezone } from '~/src/dash/utils/timezone';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EReportDelivery,
  EReportFrequency,
  EReportPeriod,
  getReportTypeCategory,
} from '~/src/dash/views/reporting/common';
import {
  IncludeExcludeStatus,
  IOrderBy,
  IReportFilterState,
} from '~/src/dash/views/reporting/ReportBuilder/common';
import { checkShouldUIDisableMetric } from '~/src/dash/views/reporting/ReportBuilder/config';
import { ReportBuilderContext } from '~/src/dash/views/reporting/ReportBuilder/context';
import { CustomizeDataSection } from '~/src/dash/views/reporting/ReportBuilder/CustomizeData/index';
import { DimensionsSection } from '~/src/dash/views/reporting/ReportBuilder/Dimensions/index';
import {
  EReportFilterApiTypes,
  REPORT_FILTERS,
} from '~/src/dash/views/reporting/ReportBuilder/Filters/filters';
import { IAS_REPORT_FILTERS } from '~/src/dash/views/reporting/ReportBuilder/Filters/ias-filters';
import { FiltersSection } from '~/src/dash/views/reporting/ReportBuilder/Filters/index';
import { MetricsSection } from '~/src/dash/views/reporting/ReportBuilder/Metrics/index';
import { OverviewSection } from '~/src/dash/views/reporting/ReportBuilder/Overview/index';
import { QuerySection } from '~/src/dash/views/reporting/ReportBuilder/Query/index';
import { ReportingTopBar } from '~/src/dash/views/reporting/ReportBuilder/ReportingTopBar';
import { ScheduleSection } from '~/src/dash/views/reporting/ReportBuilder/Schedule/index';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';
import { resetMomentLocale, updateMomentLocale } from '~/src/locales/momentCfg';

interface IReportBuilderNewProps {
  report: IReport | null;
  setReport: (report: IReport) => void;
  savedAsQuickReport: boolean;
  isLoadingReport: boolean;
}

export const ReportBuilderNew = ({
  report,
  setReport,
  savedAsQuickReport,
  isLoadingReport,
}: IReportBuilderNewProps) => {
  const { t } = usePublicaTranslation();

  const { user, session, isSuperUser } = useBaseStore();
  const initialTimezone = getTimezone(user.user.Timezone, session.publisher.Timezone);
  const initialQueryDates = generateQueryDates('Last 7 Days', initialTimezone);

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(initialTimezone);
  const [isTimezoneOptionDisabled, setIsTimezoneOptionDisabled] = useState<
    (opt: ISimpleOption) => boolean
  >(() => () => false);
  const [isSuperUserOnly, setIsSuperUserOnly] = useState(
    !report?.ID && hasRoleSuperUser(user.user),
  );
  const [currency, setCurrency] = useState<string>(
    convertECurrencyTypeToString(session.publisher.CurrencyDisplayed),
  );
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [startDate, setStartDate] = useState(initialQueryDates.startDate);
  const [endDate, setEndDate] = useState(initialQueryDates.endDate);
  const [dateRange, setDateRange] = useState(DateRangeInt.DateRangeLast7Days);
  const [scheduledDay, setScheduledDay] = useState('1');
  const [subject, setEmailSubject] = useState('');
  const [reportTypes, setReportTypes] = useState<IReportType[]>([]);
  const [selectedDimensionKeys, setSelectedDimensionKeys] = useState<string[]>([]);
  const [selectedMetricKeys, setSelectedMetricKeys] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<IReportFilterState[]>([]);
  const [selectedReportTypeID, setSelectedReportTypeID] = useState<number>(
    ReportTypeID.AuctionQuickReport,
  );
  const [orderBys, setOrderBys] = useState<IOrderBy[]>([]);
  const [limit, setLimit] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSchedulingActive, setIsSchedulingActive] = useState(false);
  const [period, setPeriod] = useState(String(EReportPeriod.LastDay));
  const [frequency, setFrequency] = useState(String(EReportFrequency.FrequencyDaily));
  const [delivery, setDelivery] = useState(String(EReportDelivery.Email));
  const [recipients, setRecipients] = useState<string[]>([]);
  const [s3AccessPoint, setS3AccessPoint] = useState<IS3AccessPoint | null>(null);
  // SNACKBAR STATE
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState<null>();
  const [snackbarState, setSnackbarState] = useState<any>(null);

  const reportTypeNotInitialLoad = useRef({} as Record<number, boolean>);

  // error handler
  const onError = (err: any, message: string) => {
    setError(err);
    setSnackbarState('error');
    setSnackbarMessage(message);
  };

  // success handler
  const onSuccess = (message: string) => {
    setError(null);
    setSnackbarState('success');
    setSnackbarMessage(message);
  };

  // loading handler
  const onLoading = (msg: string) => {
    setError(null);
    setSnackbarState('processing');
    setSnackbarMessage(msg);
  };

  const isDimensionDisabled = (id: string, subheader: string, selectedDimensions: string[]) => {
    const timeRangeKeys = ['day', 'hour', 'week_of_year', 'month', 'half_hour'];
    const presetAggregrateKeys = [
      'preset_aggregate_yesterday',
      'preset_aggregate_last_seven_days',
      'preset_aggregate_last_thirty_days',
    ];
    const isTimeRangeSelected = selectedDimensions.some((key) => timeRangeKeys.includes(key));
    const isPresetAggregateSelected = selectedDimensions.some((key) =>
      presetAggregrateKeys.includes(key),
    );
    if (
      (isTimeRangeSelected &&
        !selectedDimensions.includes(id) &&
        subheader === 'Time Range' &&
        timeRangeKeys.includes(id)) ||
      (isPresetAggregateSelected &&
        !selectedDimensions.includes(id) &&
        subheader === 'Preset Aggregate' &&
        presetAggregrateKeys.includes(id))
    ) {
      return true;
    }
    return false;
  };

  const selectedReportType = reportTypes.find(
    (reportType) => reportType.ID === selectedReportTypeID,
  );
  const reportTypeCategory = getReportTypeCategory(selectedReportType?.Category as number);
  const reportTypesList = reportTypes.map((reportType) => {
    return {
      key: 'reportType',
      value: String(reportType.ID),
      label: reportType.Label,
      category: reportType.Category,
      description: reportType.Description,
      tKey: reportTypeIDToTKey[String(reportType.ID)],
    };
  });

  const dimensions = selectedReportType?.Dimensions.map((dimension) => {
    return {
      ID: dimension.Key,
      selected:
        (!reportTypeNotInitialLoad.current[selectedReportTypeID] &&
          dimension.IsSelectedByDefault) ||
        dimension.IsSelectedRequired ||
        selectedDimensionKeys.includes(dimension.Key),
      title: dimension.Label,
      subheader: dimension.Subheader,
      disabled:
        dimension.IsSelectedRequired ||
        isDimensionDisabled(dimension.Key, dimension.Subheader, selectedDimensionKeys),
    };
  });

  const disabledMetricKeys: string[] = [];

  const metrics =
    selectedReportType?.Metrics?.map((metric) => {
      const isMetricDisabled =
        metric.IsSelectedRequired ||
        checkShouldUIDisableMetric(selectedReportTypeID, metric.Key, selectedDimensionKeys);
      if (isMetricDisabled && !metric.IsSelectedRequired) {
        disabledMetricKeys.push(metric.Key);
      }

      const isMetricSelected =
        metric.IsSelectedRequired ||
        (!isMetricDisabled &&
          ((!reportTypeNotInitialLoad.current[selectedReportTypeID] &&
            metric.IsSelectedByDefault) ||
            selectedMetricKeys.includes(metric.Key)));

      return {
        ID: metric.Key,
        disabled: isMetricDisabled,
        selected: isMetricSelected,
        title: metric.Label,
        subheader: metric.Subheader,
      };
    }) || [];

  const selectedMetrics = metrics
    .filter((metric) => metric.selected)
    .map((metric) => {
      return {
        ID: metric.ID,
        selected: false,
        title: metric.title,
        subheader: metric.subheader,
      };
    });
  const fetchS3AccessPoint = () => {
    const isTTDReport = EBool.fromBool(selectedReportTypeID === ReportTypeID.TTDReport);
    getS3AccessPoint(session.publisher.ID, isTTDReport)
      .then((val: IHttpResponse<IS3AccessPoint>) => {
        setS3AccessPoint(val.parsedBody as IS3AccessPoint);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // get the time range when a preset aggregate dimension is selected
  const getQueryDatesFromPresetAggregateDimension = (
    key: string,
  ): Record<string, moment.Moment> => {
    switch (key) {
      case 'preset_aggregate_yesterday': {
        const { startDate, endDate } = generateQueryDates('Yesterday', timezone);
        return { startDate, endDate };
      }

      case 'preset_aggregate_last_seven_days': {
        const { startDate, endDate } = generateQueryDates('Last 7 Days', timezone);
        return { startDate, endDate };
      }

      case 'preset_aggregate_last_thirty_days': {
        const { startDate, endDate } = generateQueryDates('Last 30 Days', timezone);
        return { startDate, endDate };
      }

      default: {
        const { startDate, endDate } = generateQueryDates('Today', timezone);
        return { startDate, endDate };
      }
    }
  };

  // move this to top wrapper when ready
  updateMomentLocale(t);

  // this resets it back to Sun-Sat on unmount
  useEffect(() => {
    return () => {
      resetMomentLocale();
    };
  }, []);

  useEffect(() => {
    if (report && savedAsQuickReport) {
      onSuccess(t('ReportingSnackbarSavedAsQuickReport'));
    }
  }, [report]);

  // refetch the S3 detail when publisher or reportType changes
  useEffect(() => {
    fetchS3AccessPoint();
  }, [session.publisher.ID, selectedReportTypeID]);

  // get report types
  useEffect(() => {
    getReportTypes(session.publisher.ID)
      .then((reportTypes) => {
        if (reportTypes) {
          unstable_batchedUpdates(() => {
            setReportTypes(reportTypes);
            setIsLoading(false);
            track(
              'Report List - Open Report',
              {
                id: user.user.ID,
                is_super_user: isSuperUser,
              },
              user.user,
            );
          });
        }
      })
      .catch((e) => {
        unstable_batchedUpdates(() => {
          setError(e);
          setSnackbarMessage(t('ErrorLoadingItem', { item: t('ReportTypes') }));
        });
      });
  }, []);

  // get exchange rate whenever currency changes
  useEffect(() => {
    getExchangeRate(convertStringToECurrencyType(currency))
      .then((exchange: IExchangeRate) => {
        setExchangeRate(exchange.ExchangeValueToUSD);
      })
      .catch((e) => {
        unstable_batchedUpdates(() => {
          setError(e);
          setSnackbarMessage(t('ErrorLoadingItem', { item: t('CurrencyExchangeRate') }));
        });
      });
  }, [currency]);

  // on report type change filter our dimensions/metrics/filters
  // that don't belong to the new report type
  useEffect(() => {
    const newSelectedMetrics =
      selectedReportType?.Metrics?.filter(
        (metric) =>
          (!reportTypeNotInitialLoad.current[selectedReportTypeID] && metric.IsSelectedByDefault) ||
          metric.IsSelectedRequired ||
          selectedMetricKeys.includes(metric.Key),
      ) || [];

    const newSelectedDimensions =
      selectedReportType?.Dimensions?.filter(
        (dimension) =>
          (!reportTypeNotInitialLoad.current[selectedReportTypeID] &&
            dimension.IsSelectedByDefault) ||
          dimension.IsSelectedRequired ||
          selectedDimensionKeys.includes(dimension.Key),
      ) || [];

    const availableFilters = selectedReportType?.Filters.map((filter) => filter.Key) || [];
    reportTypeNotInitialLoad.current[selectedReportTypeID] = true;

    // if reportType has Timezone in its config, it is the only timezone the report can use
    if (selectedReportType?.Timezone && selectedReportType?.Timezone !== '') {
      setTimezone(selectedReportType?.Timezone);
      setIsTimezoneOptionDisabled(
        () => (opt: ISimpleOption) => opt.key !== selectedReportType?.Timezone,
      );
    } else {
      setIsTimezoneOptionDisabled(() => () => false);
    }

    // if reportType has Timezone in its config, it is the only timezone the report can use
    if (selectedReportType?.Timezone && selectedReportType?.Timezone !== '') {
      setTimezone(selectedReportType?.Timezone);
      setIsTimezoneOptionDisabled(
        () => (opt: ISimpleOption) => opt.key !== selectedReportType?.Timezone,
      );
    }

    unstable_batchedUpdates(() => {
      setSelectedMetricKeys(newSelectedMetrics.map((metric) => metric.Key));
      setSelectedDimensionKeys(newSelectedDimensions.map((dimension) => dimension.Key));
      setSelectedFilters(selectedFilters.filter((filter) => availableFilters.includes(filter.key)));
    });
  }, [selectedReportType]);

  // update startDate & endDate when a preset aggregate dimension is selected
  useEffect(() => {
    const presetAggeregateKey = selectedDimensionKeys.filter((dimensionKey) =>
      dimensionKey.includes('preset_aggregate'),
    )[0];
    if (presetAggeregateKey) {
      const { startDate, endDate } = getQueryDatesFromPresetAggregateDimension(presetAggeregateKey);
      setEndDate(endDate);
      setStartDate(startDate);
    }
  }, [selectedDimensionKeys]);

  // load report if report exists
  useEffect(() => {
    if (report) {
      const loadedLimit = Number(report.ReportLimit);
      const loadedOrderBys = loadOrderBys(report.ReportOrderBy);

      // if report has a valid DateRange, use that to set the initial start and end dates
      // otherwise, use report's StartDate and EndDate
      let loadedStartDate = moment(report.StartDate);
      let loadedEndDate = moment(report.EndDate);
      if (report.DateRange !== DateRangeInt.DateRangeUnknown) {
        const loadedDateRange = findDateRange(
          DateRangeIntToDateRange[report.DateRange],
        ).generateQueryDates(timezone);
        loadedStartDate = loadedDateRange.startDate;
        loadedEndDate = loadedDateRange.endDate;
      }
      unstable_batchedUpdates(() => {
        setName(report.Name);
        setStartDate(loadedStartDate);
        setEndDate(loadedEndDate);
        setDateRange(report.DateRange);
        setTimezone(report.Timezone);
        setCurrency(convertECurrencyTypeToString(+report.CurrencyDisplayed));
        setSelectedReportTypeID(+report.ReportType);
        setSelectedDimensionKeys(report.GroupByOptions);
        setSelectedFilters(loadFiltersFromReport(report));
        setSelectedMetricKeys(report.Metrics);
        if (loadedLimit > 0) {
          setLimit(loadedLimit);
        }
        if (loadedOrderBys.length > 0) {
          setOrderBys(loadedOrderBys);
        }
        setIsSuperUserOnly(EBool.isTrue(report.SuperOnly));
        setPeriod(report.Period);
        setRecipients(report.Recipients);
        setFrequency(report.Frequency);
        setScheduledDay(report.ScheduledDay);
        setEmailSubject(report.Subject);
        setIsSchedulingActive(report.Active === 'true' ? true : false);
        setDelivery(String(report.ShouldUploadToExternalS3));
      });
    }
  }, [report]);

  const loadOrderBys = (loadedOrderBysStr: string): IOrderBy[] => {
    if (loadedOrderBysStr.length === 0) {
      return [];
    }
    const loadedOrderBys = loadedOrderBysStr.split(',');

    return loadedOrderBys.map((orderByStr) => {
      const [key, direction] = orderByStr.split(' ');

      return {
        key,
        direction: direction === 'ASC' ? 'Ascending' : 'Descending',
      };
    });
  };

  const loadFiltersFromReport = (report: IReport): IReportFilterState[] => {
    const loadedFilters: IReportFilterState[] = [];

    const getLoadedFilter = (
      key: string,
      selected: string[],
      includeExcludeStatus: IncludeExcludeStatus,
      isHideInactive: boolean,
    ) => {
      return {
        key,
        selected,
        includeExcludeStatus,
        isHideInactive,
      };
    };

    const getIncludeExcludeStatus = (key: string): IncludeExcludeStatus => {
      if (key === 'bidder_ids') {
        return report.IsBidderIDsIncluded === EBool.EBool_TRUE
          ? IncludeExcludeStatus.INCLUDE
          : IncludeExcludeStatus.EXCLUDE;
      } else if (key === 'site_ids') {
        return report.IsSiteIDsIncluded === EBool.EBool_TRUE
          ? IncludeExcludeStatus.INCLUDE
          : IncludeExcludeStatus.EXCLUDE;
      }
      return IncludeExcludeStatus.NONE;
    };

    const getIsHideInactive = (key: string): boolean => {
      if (key === 'bidder_ids') {
        return isTrue(report.ReportFilterConfig.BidderActiveOnly);
      } else if (key === 'site_ids') {
        return isTrue(report.ReportFilterConfig.ChannelActiveOnly);
      }
      return false;
    };

    REPORT_FILTERS.forEach((filter) => {
      const key = filter.key;
      const includeExcludeStatus = getIncludeExcludeStatus(key);
      const isHideInactive = getIsHideInactive(key);

      // channel groups is loaded in as a list of ChannelGroups
      // not as a list of IDs like the other filters
      if (filter.key === 'channel_group_ids') {
        const cgFilter = selectedFilters.find((filter) => filter.key === 'channel_group_ids');
        if (report.ChannelGroups.length > 0 || (cgFilter && cgFilter.selected.length > 0)) {
          loadedFilters.push(
            getLoadedFilter(
              key,
              // when we update a report, the updated report's ChannelGroups list
              // doesn't have the new channel groups so we grab the value from state instead
              cgFilter?.selected || report.ChannelGroups.map((cg) => String(cg.ID)),
              includeExcludeStatus,
              isHideInactive,
            ),
          );
        }
      }

      // if the filter is in the reportFilterConfig place it there,
      // otherwise place it in the report
      if (filter.isApiKeyInReportFilterConfig && filter.apiKey) {
        // depending on what type of data the API returns, parse it out
        // and load it into state
        // @ts-ignore
        if (report.ReportFilterConfig[filter.apiKey]) {
          // @ts-ignore
          if (typeof report.ReportFilterConfig[filter.apiKey] === 'number') {
            loadedFilters.push(
              getLoadedFilter(
                key,
                // @ts-ignore
                [String(report.ReportFilterConfig[filter.apiKey])],
                includeExcludeStatus,
                isHideInactive,
              ),
            );
          } else if (
            // @ts-ignore
            report.ReportFilterConfig[filter.apiKey].length > 0 &&
            // @ts-ignore
            typeof report.ReportFilterConfig[filter.apiKey] === 'string'
          ) {
            loadedFilters.push(
              getLoadedFilter(
                key,
                // @ts-ignore
                [report.ReportFilterConfig[filter.apiKey]],
                includeExcludeStatus,
                isHideInactive,
              ),
            );
          } else if (
            // @ts-ignore
            report.ReportFilterConfig[filter.apiKey].length > 0 &&
            // @ts-ignore
            typeof report.ReportFilterConfig[filter.apiKey] === 'object'
          ) {
            loadedFilters.push(
              getLoadedFilter(
                key,
                // @ts-ignore
                report.ReportFilterConfig[filter.apiKey],
                includeExcludeStatus,
                isHideInactive,
              ),
            );
          }
        }
      } else {
        // depending on what type of data the API returns, parse it out
        // and load it into state
        // @ts-ignore
        if (report[filter.apiKey]) {
          // @ts-ignore
          if (typeof report[filter.apiKey] === 'number') {
            loadedFilters.push(
              getLoadedFilter(
                key,
                // @ts-ignore
                [String(report[filter.apiKey])],
                includeExcludeStatus,
                isHideInactive,
              ),
            );
          } else if (
            // @ts-ignore
            report[filter.apiKey].length > 0 &&
            // @ts-ignore
            typeof report[filter.apiKey] === 'string'
          ) {
            loadedFilters.push(
              getLoadedFilter(
                key,
                // @ts-ignore
                [report[filter.apiKey]],
                includeExcludeStatus,
                isHideInactive,
              ),
            );
          } else if (
            // @ts-ignore
            report[filter.apiKey].length > 0 &&
            // @ts-ignore
            typeof report[filter.apiKey] === 'object'
          ) {
            loadedFilters.push(
              getLoadedFilter(
                key,
                // @ts-ignore
                report[filter.apiKey],
                includeExcludeStatus,
                isHideInactive,
              ),
            );
          }
        }
      }
    });

    IAS_REPORT_FILTERS.forEach((filter) => {
      if (report.IASReportFilterConfig[filter.apiKey]?.Selected?.length > 0) {
        loadedFilters.push(
          getLoadedFilter(
            filter.key,
            report.IASReportFilterConfig[filter.apiKey].Selected.split(','),
            getIncludeExcludeStatus(filter.key),
            getIsHideInactive(filter.key),
          ),
        );
      }
    });

    return loadedFilters;
  };

  const buildReport = (): IReport => {
    const buildInitialReportFromFilters = () => {
      const report: any = {
        ReportFilterConfig: {},
        IASReportFilterConfig: {},
      };

      REPORT_FILTERS.forEach((filter) => {
        if (filter.apiKey) {
          const foundFilter = selectedFilters.find((f) => f.key === filter.key);
          if (filter.isApiKeyInReportFilterConfig) {
            switch (filter.apiType) {
              case EReportFilterApiTypes.number:
                report.ReportFilterConfig[filter.apiKey] = foundFilter
                  ? +foundFilter.selected[0]
                  : null;
                break;
              case EReportFilterApiTypes.string:
                report.ReportFilterConfig[filter.apiKey] = foundFilter
                  ? foundFilter.selected[0]
                  : '';
                break;
              case EReportFilterApiTypes.stringList:
                report.ReportFilterConfig[filter.apiKey] = foundFilter ? foundFilter.selected : [];
                break;
              case EReportFilterApiTypes.numberList:
                report.ReportFilterConfig[filter.apiKey] = foundFilter
                  ? foundFilter.selected.map((item) => +item)
                  : [];
                break;
            }
          } else {
            switch (filter.apiType) {
              case EReportFilterApiTypes.number:
                report[filter.apiKey] = foundFilter ? +foundFilter.selected[0] : null;
                break;
              case EReportFilterApiTypes.string:
                report[filter.apiKey] = foundFilter ? foundFilter.selected[0] : '';
                break;
              case EReportFilterApiTypes.stringList:
                report[filter.apiKey] = foundFilter ? foundFilter.selected : [];
                break;
              case EReportFilterApiTypes.numberList:
                report[filter.apiKey] = foundFilter
                  ? foundFilter.selected.map((item) => +item)
                  : [];
                break;
            }
          }
        }
      });

      IAS_REPORT_FILTERS.forEach((filter) => {
        const foundFilter = selectedFilters.find((f) => f.key === filter.key);
        if (foundFilter && foundFilter.selected.length > 0) {
          report.IASReportFilterConfig[filter.apiKey] = {
            Key: foundFilter.key,
            Selected: foundFilter.selected.join(','),
          };
        }
      });
      return report;
    };

    const newReport = buildInitialReportFromFilters();
    newReport.ID = report ? report.ID : '0';
    newReport.Name = name;
    newReport.StartDate = startDate.format('YYYY-MM-DDTHH:mm:ssZ');
    newReport.EndDate = endDate.format('YYYY-MM-DDTHH:mm:ssZ');
    newReport.DateRange = dateRange;
    newReport.UpdatedBy = user.user.Username;
    newReport.GroupByOptions = selectedDimensionKeys;
    newReport.Metrics = selectedMetricKeys;
    (newReport.ReportOrderBy = orderBys
      .filter((orderBy) => orderBy.key !== '')
      .map((orderBy) => `${orderBy.key} ${orderBy.direction === 'Ascending' ? 'ASC' : 'DESC'}`)
      .join(',')),
      (newReport.Period = period);
    newReport.Active = isSchedulingActive ? '1' : '0';
    newReport.Recipients = recipients;
    newReport.Frequency = frequency;
    newReport.CurrencyDisplayed = convertStringToECurrencyType(currency);
    newReport.ReportType = String(selectedReportTypeID);
    newReport.SuperOnly = isSuperUserOnly ? EBool.EBool_TRUE : EBool.EBool_FALSE;
    newReport.Timezone = timezone;
    newReport.Subject = subject;
    newReport.ShouldUploadToExternalS3 =
      isSchedulingActive && delivery !== String(EReportDelivery.Email)
        ? EBool.EBool_TRUE
        : EBool.EBool_FALSE;
    newReport.ScheduledDay = scheduledDay;
    newReport.ReportLimit = String(limit);
    newReport.CreatedAt = '';
    newReport.UpdatedAt = '';
    newReport.DeletedAt = '';
    newReport.SSP = '';
    newReport.ExchangeRate = {} as IExchangeRate;
    newReport.AdsourceIDs = '';
    newReport.Positions = [];
    newReport.DemandTypes = [];
    newReport.ChannelGroups = [];
    newReport.User = user.user;
    newReport.UserID = +user.user.ID;

    // other filter stuff
    // Is_IDsIncluded should default to true if IncludeExcludeStatus is undefined, NONE, or
    // INCLUDE due to backend code
    newReport.IsBidderIDsIncluded =
      selectedFilters.find((filter) => filter.key === 'bidder_ids')?.includeExcludeStatus ===
      IncludeExcludeStatus.EXCLUDE
        ? EBool.EBool_FALSE
        : EBool.EBool_TRUE;
    newReport.IsSiteIDsIncluded =
      selectedFilters.find((filter) => filter.key === 'site_ids')?.includeExcludeStatus ===
      IncludeExcludeStatus.EXCLUDE
        ? EBool.EBool_FALSE
        : EBool.EBool_TRUE;
    newReport.ReportFilterConfig.BidderActiveOnly =
      selectedFilters.find((filter) => filter.key === 'bidder_ids')?.isHideInactive || false;
    newReport.ReportFilterConfig.ChannelActiveOnly =
      selectedFilters.find((filter) => filter.key === 'site_ids')?.isHideInactive || false;

    return newReport as IReport;
  };

  return (
    <ReportBuilderContext.Provider
      value={{
        endDate,
        reportTypeID: selectedReportTypeID,
        startDate,
        timezone,
      }}
    >
      <Panels max vertical relative minWidthMain>
        <Panel largeHorizontalPaddingV2 smallVerticalPaddingV2>
          <TopBar title={t('Reporting')} />
        </Panel>

        <Panel
          largeHorizontalPaddingV2
          style={{
            borderBottom: `1px solid ${publicaColors.lightGray}`,
            paddingTop: spacingV2.extraSmall,
            paddingBottom: spacingV2.small,
          }}
        >
          <ReportingTopBar
            graphID={selectedReportType?.GraphID || 0}
            isLoading={isLoading}
            setName={setName}
            isSchedulingActive={isSchedulingActive}
            reportTypeCategory={reportTypeCategory}
            selectedMetrics={selectedMetricKeys}
            selectedDimensions={selectedDimensionKeys}
            isIasReport={selectedReportType?.IsIASReport}
            exchangeRate={exchangeRate}
            report={buildReport()}
            setReport={setReport}
            onSuccess={onSuccess}
            onError={onError}
            onLoading={onLoading}
          />
        </Panel>

        <Panel scroll largeHorizontalPaddingV2 verticalPaddingV2>
          <Panels vertical relative minWidthMain>
            <Panel highlighted marginBottom={spacingV2.large} enableSize>
              <Rows>
                <Row
                  style={{
                    borderBottom: `1px solid ${publicaColors.lightGray}`,
                  }}
                >
                  <OverviewSection
                    isTimezoneOptionDisabled={isTimezoneOptionDisabled}
                    setTimezone={setTimezone}
                    currency={currency}
                    setCurrency={setCurrency}
                    isSuperUserOnly={isSuperUserOnly}
                    setIsSuperUserOnly={setIsSuperUserOnly}
                    isLoading={isLoading || isLoadingReport}
                    // @ts-ignore
                    createdBy={report?.User?.username || ''}
                    createdAt={report ? formatDateUsingTimezoneV2(report.CreatedAt, timezone) : ''}
                    updatedAt={report ? formatDateUsingTimezoneV2(report.UpdatedAt, timezone) : ''}
                    updatedBy={report ? report?.UpdatedBy : ''}
                    period={period}
                    frequency={frequency}
                    isSchedulingActive={isSchedulingActive}
                    enableCurrency={selectedReportType?.EnableCurrency}
                    enableTimezone={selectedReportType?.EnableTimezone}
                  />
                </Row>
                <Row
                  style={{
                    borderBottom: `1px solid ${publicaColors.lightGray}`,
                  }}
                >
                  <QuerySection
                    reportTypes={reportTypesList}
                    reportTypeCategory={reportTypeCategory}
                    setSelectedReportTypeID={setSelectedReportTypeID}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setDateRange={setDateRange}
                    isLoading={isLoading || (isLoadingReport && isEmpty(report))}
                    showCalendar={selectedReportType?.EnableCalendarSetting}
                    calendarSetting={selectedReportType?.CalendarSetting}
                    isCalendarDisabled={selectedDimensionKeys.some((key) =>
                      key.includes('preset_aggregate'),
                    )}
                    isIasReport={selectedReportType?.IsIASReport}
                    onError={onError}
                  />
                </Row>
                <Row
                  style={{
                    borderBottom: `1px solid ${publicaColors.lightGray}`,
                  }}
                >
                  <FiltersSection
                    filters={selectedReportType?.Filters || []}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    selectableFilters={
                      selectedReportType?.Filters.map((filter) => filter.Key) || []
                    }
                    onError={onError}
                  />
                </Row>
                <Row
                  style={{
                    borderBottom: `1px solid ${publicaColors.lightGray}`,
                  }}
                >
                  <DimensionsSection
                    dimensions={dimensions || []}
                    selectedDimensionKeys={selectedDimensionKeys}
                    setSelectedDimensionKeys={setSelectedDimensionKeys}
                    isLoading={isLoading}
                    report={report}
                  />
                </Row>
                <Row
                  style={{
                    borderBottom: `1px solid ${publicaColors.lightGray}`,
                  }}
                >
                  <MetricsSection
                    metrics={metrics || []}
                    selectedMetricKeys={selectedMetricKeys}
                    setSelectedMetricKeys={setSelectedMetricKeys}
                    orderBys={orderBys}
                    setOrderBys={setOrderBys}
                    isLoading={isLoading}
                    report={report}
                    disabledMetricKeys={disabledMetricKeys}
                  />
                </Row>
                <Row>
                  <CustomizeDataSection
                    selectedMetrics={selectedMetrics || []}
                    orderBys={orderBys}
                    setOrderBys={setOrderBys}
                    limit={limit}
                    setLimit={setLimit}
                    isLoading={isLoading || isLoadingReport}
                  />
                </Row>
              </Rows>
            </Panel>

            <Panel basePaddingV2 highlighted>
              <ScheduleSection
                isIasReport={selectedReportType?.IsIASReport}
                isSchedulingActive={isSchedulingActive}
                scheduledDay={scheduledDay}
                frequency={frequency}
                period={period}
                delivery={delivery}
                recipients={recipients}
                isLoading={isLoading}
                subject={subject}
                setEmailSubject={setEmailSubject}
                setScheduledDay={setScheduledDay}
                setIsSchedulingActive={setIsSchedulingActive}
                setPeriod={setPeriod}
                setFrequency={setFrequency}
                setDelivery={setDelivery}
                setRecipients={setRecipients}
                s3AccessPoint={s3AccessPoint}
              />
            </Panel>
            <div style={{ position: 'relative', minHeight: '1.5rem' }}>{''}</div>
          </Panels>
        </Panel>
        <SnackbarView
          open={!!snackbarMessage}
          error={error}
          onClose={() => {
            setSnackbarMessage('');
            setSnackbarState(null);
          }}
          info={snackbarState === 'info'}
          processing={snackbarState === 'processing'}
          autoHideDuration={snackbarState === 'processing' ? 300000 : 6000} // longer wait time for the snack bar when downloading report
        >
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    </ReportBuilderContext.Provider>
  );
};
